/**
 * 20261030 학생 송출 SDK 작업 대상 현재성·송출 트랙 사용 판정 (web-relay R-262·R-264·R-266·R-267, milestone_42 §3.41·§3.44·§3.46)
 *
 * 왜 있나: 송출 단계(produce·replaceTrack·quality)는 await 를 거친다. 그 사이 종료·새 owner(B)·같은 owner 의
 *          transport 복구(recreateSendTransport)가 전역을 바꿀 수 있다. 늦게 끝난 작업이 현재 Producer/status 를
 *          덮지 않게 하고, 쓰이지 않게 된 track clone 만 회수한다.
 *
 * 계약
 * - op: SDK 호출 직전 동기 구간에서 그 호출이 쓰는 transport·Producer·track 을 잡는다(기존 전역 읽기 시점 그대로).
 * - check: intent 가 없으면(기존 무인자 호출) 항상 "ok". 있으면 owner intent → 대상 현재성 순서로 판정한다.
 * - 사용 판정: 전역 슬롯·복구 대기·등록된 살아 있는 Producer.track·진행 중 SDK 참조 중 하나라도 해당하면 사용 중.
 *              판정 중 예외는 사용 중으로 본다(정지하지 않음).
 * - 회수: 사용 중인 clone 은 후보로 두고, Producer close·SDK 참조 해제·종료 onSettled 에서 후보별로 다시 판정한다.
 * - 기록·재판정의 예외는 로그만 남기고 기존 SDK 반환/예외를 덮지 않는다.
 * 판단만 한다 — 전역도 import 도 없다. 현재 값은 주입된 getter 로 읽는다.
 */

export const SEND_OP_RESULT = Object.freeze({
    OK           : "ok",
    OWNER_INVALID: "owner-invalid",
    TARGET_STALE : "target-stale"
});

/**
 * @param {Object}   deps
 * @param {Function} deps.isIntentValid - (intent) → Boolean
 * @param {Function} deps.getCurrent    - () → { transport, audioProducer, videoProducer, audioTrack, videoTrack, pending }
 * @param {Function} [deps.log]
 */
export const createSendOpGuard = (deps) => {
    const log = deps.log || ((msg) => console.log(msg));
    const holds = new Map();          // track → count
    const liveProducers = new Set();  // produce() 가 만든, 아직 닫히지 않은 Producer
    const unknownProducers = new Set(); // observer 등록에 실패한 Producer(불명)
    const candidates = new Set();     // 회수 후보 track

    const safeLog = (msg) => { try { log(msg); } catch (err) { /* 로그 실패 무시 */ } };

    const isClosed = (obj) => !obj || obj.closed === true;

    const current = () => deps.getCurrent() || {};

    /** SDK 호출 직전 동기 구간에서 부른다. */
    const captureSendOp = (type, kind, values) => Object.freeze({ type, kind, ...values });

    /**
     * @param {Object} intent - 없으면 기존 무인자 호출
     * @param {Object} op
     * @returns {String} SEND_OP_RESULT
     */
    const checkSendOp = (intent, op) => {
        if (intent === undefined || intent === null) return SEND_OP_RESULT.OK;
        let isValid = false;
        try {
            isValid = deps.isIntentValid(intent) === true;
        } catch (err) {
            isValid = false;
        }
        if (!isValid) return SEND_OP_RESULT.OWNER_INVALID;
        if (!op) return SEND_OP_RESULT.OK;
        try {
            const cur = current();
            const slotTrack = op.kind === "audio" ? cur.audioTrack : cur.videoTrack;
            const slotProducer = op.kind === "audio" ? cur.audioProducer : cur.videoProducer;
            if (op.track !== undefined && slotTrack !== op.track) return SEND_OP_RESULT.TARGET_STALE;
            if (op.type === "produce") {
                if (cur.transport !== op.transport || isClosed(op.transport)) return SEND_OP_RESULT.TARGET_STALE;
                if (slotProducer !== null && slotProducer !== undefined) return SEND_OP_RESULT.TARGET_STALE;
            } else if (op.type === "replace") {
                if (slotProducer !== op.producer || isClosed(op.producer)) return SEND_OP_RESULT.TARGET_STALE;
            }
            return SEND_OP_RESULT.OK;
        } catch (err) {
            safeLog(`sendOpGuard - 대상 판정 오류, 결과를 반영하지 않는다. ${err && err.message}`);
            return SEND_OP_RESULT.TARGET_STALE;
        }
    };

    /** 판정 중 예외는 사용 중으로 본다. */
    const isTrackInUse = (track) => {
        if (!track) return false;
        try {
            if ((holds.get(track) || 0) > 0) return true;
            const cur = current();
            if (cur.audioTrack === track || cur.videoTrack === track) return true;
            if (cur.pending && (cur.pending.audioTrack === track || cur.pending.videoTrack === track)) return true;
            const producers = [cur.audioProducer, cur.videoProducer];
            liveProducers.forEach((p) => producers.push(p));
            for (let i = 0; i < producers.length; i++) {
                const p = producers[i];
                if (p && !isClosed(p) && p.track === track) return true;
            }
            let unknownInUse = false;
            unknownProducers.forEach((p) => {
                if (isClosed(p)) unknownProducers.delete(p); // closed 로 확인되면 불명 등록을 푼다
                else if (p.track === track || p.track === undefined) unknownInUse = true;
            });
            return unknownInUse;
        } catch (err) {
            safeLog(`sendOpGuard - 사용 판정 오류, 사용 중으로 본다. ${err && err.message}`);
            return true;
        }
    };

    const stopTrack = (track, name) => {
        try {
            if (track && track.readyState !== "ended") track.stop();
            return true;
        } catch (err) {
            safeLog(`sendOpGuard - ${name} 정지 실패 ${err && err.message}`);
            return false;
        }
    };

    /** 정지가 실제로 끝났는지(예외 없음 + ended) 확인한다. 20261030 (R-270) */
    const stopAndConfirm = (track, name) => stopTrack(track, name) && track.readyState === "ended";

    /** 후보별로 다시 판정해 고아만 정지하고, 정지가 확인된 후보만 지운다. 집합 전체를 비우지 않는다. */
    const sweep = (why) => {
        try {
            Array.from(candidates).forEach((track) => {
                if (isTrackInUse(track)) return;
                if (!stopAndConfirm(track, "candidate")) { safeLog(`sendOpGuard - 고아 track 정지 실패(${why}). 후보로 남긴다. 후보 ${candidates.size}`); return; } // 20261030 실패는 성공으로 세지 않는다 (R-270)
                candidates.delete(track);
                safeLog(`sendOpGuard - 고아 track 회수(${why}). 남은 후보 ${candidates.size}`);
            });
        } catch (err) {
            safeLog(`sendOpGuard - 후보 재판정 오류 ${err && err.message}`);
        }
    };

    /** 진행 중 SDK 작업이 track 을 잡는다. 반환값은 1회만 동작하는 해제 함수. */
    const holdSendTrack = (track) => {
        if (!track) return () => {};
        try {
            holds.set(track, (holds.get(track) || 0) + 1);
        } catch (err) {
            safeLog(`sendOpGuard - 참조 기록 실패 ${err && err.message}`);
            return () => {};
        }
        let isReleased = false;
        return () => {
            if (isReleased) return;
            isReleased = true;
            try {
                const n = (holds.get(track) || 0) - 1;
                if (n > 0) holds.set(track, n);
                else holds.delete(track);
            } catch (err) {
                safeLog(`sendOpGuard - 참조 해제 실패 ${err && err.message}`);
            }
            sweep("sdk-release");
        };
    };

    /** produce() 가 반환 전에 부른다. Producer 가 닫힐 때까지 그 track 을 보호한다(R-267). */
    const registerSendProducer = (producer) => {
        if (!producer) return;
        try {
            if (isClosed(producer)) return; // 20261030 closed 조회도 격리 안에서 (R-271)
            const observer = producer.observer;
            if (!observer || typeof observer.on !== "function") {
                unknownProducers.add(producer);
                safeLog("sendOpGuard - observer 없는 Producer 는 불명으로 둔다.");
                return;
            }
            liveProducers.add(producer);
            observer.on("close", () => {
                liveProducers.delete(producer);
                sweep("producer-close");
            });
        } catch (err) {
            unknownProducers.add(producer);
            safeLog(`sendOpGuard - Producer 등록 실패, 불명으로 둔다. ${err && err.message}`);
        }
    };

    /** stale 뒤 호출부가 자기 clone 을 회수할 때 부른다. 사용 중이면 후보로만 둔다. */
    const reclaimTrack = (track, name) => {
        if (!track) return;
        if (isTrackInUse(track)) {
            candidates.add(track);
            safeLog(`sendOpGuard - ${name} 은 아직 사용 중이라 후보로 둔다. 후보 ${candidates.size}`);
            return;
        }
        if (!stopAndConfirm(track, name)) { // 20261030 정지 실패면 후보로 남겨 다음 재판정에서 다시 본다 (R-270)
            candidates.add(track);
            safeLog(`sendOpGuard - ${name} 정지 실패. 후보로 남긴다. 후보 ${candidates.size}`);
        }
    };

    return {
        captureSendOp,
        checkSendOp,
        holdSendTrack,
        registerSendProducer,
        isTrackInUse,
        reclaimTrack,
        sweep,
        /** 시험용 */
        getCandidateCount: () => candidates.size,
        getHoldCount: (track) => holds.get(track) || 0
    };
};
